import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, MapPin, Clock, Calendar, Download, Info } from "lucide-react";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { MarketingPage } from "../../components/smartreach/Chrome";

const SUMMARY = [
  { l: "Attributed Visits", v: "3,052", tip: "Confirmed visits directly attributed to your campaigns." },
  { l: "Projected Visits", v: "1,240", tip: "Modeled visits estimated to be attributed to your campaigns." },
  { l: "Visitation Lift", v: "+18%", sig: true, tip: "Statistically significant lift vs. a matched control group." },
  { l: "Total Cost", v: "$45,389", tip: "Total media spent to date on this study." },
  { l: "Average eCPV", v: "$14.87", tip: "Average effective cost per confirmed visit." },
];

const BY_HOUR = [
  { k: "9a", v: 92 }, { k: "10a", v: 168 }, { k: "11a", v: 240 }, { k: "12p", v: 388 },
  { k: "1p", v: 356 }, { k: "2p", v: 210 }, { k: "3p", v: 175 }, { k: "4p", v: 198 },
  { k: "5p", v: 286 }, { k: "6p", v: 372 }, { k: "7p", v: 300 }, { k: "8p", v: 187 },
];
const BY_DAY = [
  { k: "Mon", v: 318 }, { k: "Tue", v: 296 }, { k: "Wed", v: 341 }, { k: "Thu", v: 402 },
  { k: "Fri", v: 588 }, { k: "Sat", v: 671 }, { k: "Sun", v: 436 },
];

const LOCATIONS = [
  { name: "Downtown — Main St", visits: 1148, imp: 902, click: 246, impTime: "3.2 days", clickTime: "1.1 days" },
  { name: "Northside Plaza", visits: 742, imp: 566, click: 176, impTime: "4.0 days", clickTime: "0.9 days" },
  { name: "Riverside Mall", visits: 623, imp: 498, click: 125, impTime: "3.6 days", clickTime: "1.4 days" },
  { name: "West End Center", visits: 539, imp: 401, click: 138, impTime: "4.5 days", clickTime: "1.2 days" },
];

const fmt = (n) => n.toLocaleString();

export default function FootfallSampleReport() {
  const [mode, setMode] = useState("hour");
  const data = mode === "hour" ? BY_HOUR : BY_DAY;
  const peak = Math.max(...data.map((d) => d.v));

  return (
    <MarketingPage>
      <section className="srx-csec ink" style={{ paddingBottom: 40 }} data-testid="fa-sample-report">
        <div className="srx-wrap">
          <Link to="/footfall-attribution" className="srx-link" data-testid="fa-sample-back"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--x-amber)", marginBottom: 20 }}>
            <ArrowLeft size={15} /> Back to Footfall Attribution
          </Link>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
            <div>
              <span className="srx-eyebrow" style={{ color: "var(--x-amber)", display: "block", marginBottom: 12 }}>Live sample report</span>
              <h1 style={{ fontFamily: "var(--x-fdisplay)", fontSize: 34, color: "#fff", margin: 0 }}>Sunrise Bistro — Footfall Attribution</h1>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 14, fontFamily: "var(--x-fmono)", fontSize: 12, color: "var(--x-muted-dark)" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><MapPin size={13} /> 4 locations</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Clock size={13} /> 30-day attribution window</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Calendar size={13} /> May 5 – Jan 23</span>
              </div>
            </div>
            <button className="srx-btn-secondary" data-testid="fa-sample-export" disabled
              style={{ opacity: .55, cursor: "not-allowed" }}>
              <Download size={15} /> Export (sample)
            </button>
          </div>

          {/* Summary band */}
          <div className="srx-cgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", marginTop: 30 }}>
            {SUMMARY.map((s) => (
              <div key={s.l} className="srx-ccard" data-testid={`fa-sample-stat-${s.l.split(" ")[0].toLowerCase()}`}>
                <div style={{ fontFamily: "var(--x-fmono)", fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--x-muted-dark)", display: "flex", alignItems: "center", gap: 6 }}>
                  {s.l}{s.sig && <CheckCircle2 size={13} color="#067647" />}
                </div>
                <div style={{ fontFamily: "var(--x-fdisplay)", fontSize: 30, fontWeight: 800, color: "var(--x-amber)", marginTop: 8 }}>{s.v}</div>
                <div style={{ fontSize: 12.5, color: "var(--x-muted-dark)", marginTop: 6 }}>{s.tip}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footfall graph */}
      <section className="srx-csec" data-testid="fa-sample-graph">
        <div className="srx-wrap">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 22 }}>
            <div>
              <span className="srx-eyebrow">Footfall graph</span>
              <h2 style={{ fontFamily: "var(--x-fdisplay)", fontSize: 26, marginTop: 10 }}>When your customers actually walk in</h2>
            </div>
            <div style={{ display: "inline-flex", border: "1px solid var(--x-paper-line)", borderRadius: 3, overflow: "hidden" }} data-testid="fa-sample-mode-toggle">
              <button onClick={() => setMode("hour")} data-testid="fa-sample-mode-hour"
                style={{ padding: "10px 18px", fontFamily: "var(--x-fmono)", fontSize: 12, letterSpacing: ".04em", border: "none", cursor: "pointer",
                  background: mode === "hour" ? "var(--x-ink)" : "#fff", color: mode === "hour" ? "#fff" : "var(--x-graphite)" }}>
                HOUR OF DAY
              </button>
              <button onClick={() => setMode("day")} data-testid="fa-sample-mode-day"
                style={{ padding: "10px 18px", fontFamily: "var(--x-fmono)", fontSize: 12, letterSpacing: ".04em", border: "none", cursor: "pointer",
                  background: mode === "day" ? "var(--x-ink)" : "#fff", color: mode === "day" ? "#fff" : "var(--x-graphite)" }}>
                DAY OF WEEK
              </button>
            </div>
          </div>
          <div className="srx-ccard" style={{ padding: "24px 18px 12px" }}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data} margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(16,22,28,.06)" vertical={false} />
                <XAxis dataKey="k" stroke="#8a97a4" fontSize={12} tickLine={false} />
                <YAxis stroke="#8a97a4" fontSize={12} tickLine={false} axisLine={false} tickFormatter={fmt} />
                <Tooltip cursor={{ fill: "rgba(242,163,61,.08)" }}
                  contentStyle={{ background: "#0A121C", border: "1px solid var(--x-amber-dark)", borderRadius: 4, color: "#fff" }}
                  formatter={(v) => [`${fmt(v)} visits`, "Visits"]} />
                <Bar dataKey="v" radius={[3, 3, 0, 0]}>
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.v === peak ? "#F2A33D" : "#B97418"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p style={{ fontSize: 12.5, color: "var(--x-muted-light)", marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Info size={13} /> All visits shown in each location&apos;s local timezone. Peak highlighted in amber — the moment to time offers and staffing.
            </p>
          </div>
        </div>
      </section>

      {/* Footfall table */}
      <section className="srx-csec" style={{ paddingTop: 0 }} data-testid="fa-sample-table">
        <div className="srx-wrap">
          <span className="srx-eyebrow">Footfall table</span>
          <h2 style={{ fontFamily: "var(--x-fdisplay)", fontSize: 26, margin: "10px 0 20px" }}>Visits by location</h2>
          <div className="srx-cmp-wrap">
            <table className="srx-cmp-table">
              <thead>
                <tr>
                  <th>Location</th><th>Visits</th><th>Impression visits</th><th>Click visits</th>
                  <th>Avg imp. time</th><th>Avg click time</th>
                </tr>
              </thead>
              <tbody>
                {LOCATIONS.map((l) => (
                  <tr key={l.name} data-testid={`fa-sample-row-${l.name.split(" ")[0].toLowerCase()}`}>
                    <td><strong>{l.name}</strong></td>
                    <td>{fmt(l.visits)}</td>
                    <td>{fmt(l.imp)}</td>
                    <td>{fmt(l.click)}</td>
                    <td>{l.impTime}</td>
                    <td>{l.clickTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: 16, fontSize: 13, color: "var(--x-muted-light)" }}>
            Illustrative sample data shown to demonstrate the report layout. Your live report reflects your real locations,
            spend and confirmed visits — with full methodology, match rate and confidence attached.
          </p>
          <div style={{ marginTop: 26, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/signup" className="srx-btn-primary" data-testid="fa-sample-cta">Get my Footfall report</Link>
            <Link to="/footfall-attribution" className="srx-btn-secondary" data-testid="fa-sample-learn"
              style={{ color: "var(--x-graphite)", borderColor: "var(--x-paper-line)" }}>How it works</Link>
          </div>
        </div>
      </section>
    </MarketingPage>
  );
}
